import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  generateArticleSchema, 
  generateLocalBusinessSchema, 
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateServiceSchema,
  injectSchema 
} from '@/utils/schema';

// BlogPost interface - matches the database structure
interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  slug: string;
  author: string;
  published_date: string | null;
  status: 'draft' | 'published';
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string[] | null;
  categories: string[] | null;
  tags: string[] | null;
  created_at: string | null;
  updated_at: string | null;
}

interface UseSchemaOptions {
  type: 'article' | 'local-business' | 'breadcrumb' | 'faq' | 'service' | 'multiple';
  data?: any;
  schemas?: any[];
}

export const useSchema = ({ type, data, schemas }: UseSchemaOptions) => {
  const location = useLocation();

  useEffect(() => {
    console.log('useSchema effect triggered:', { type, hasData: !!data, schemasCount: Array.isArray(schemas) ? schemas.length : 0 });
    
    try {
      const currentUrl = `${window.location.origin}${location.pathname}`;
      let schema;

      switch (type) {
        case 'article':
          if (data) {
            schema = generateArticleSchema(data as BlogPost, currentUrl);
          }
          break;
        
        case 'local-business':
          if (data) {
            const { locationName, latitude, longitude, zipCodes } = data as any;
            schema = generateLocalBusinessSchema(locationName, latitude, longitude, zipCodes);
          }
          break;
        
        case 'breadcrumb':
          if (data) {
            schema = generateBreadcrumbSchema(data as any);
          }
          break;
        
        case 'faq':
          if (data) {
            schema = generateFAQSchema(data as any);
          }
          break;
        
        case 'service':
          if (data) {
            const { serviceName, description, locationName } = data as any;
            schema = generateServiceSchema(serviceName, description, locationName);
          }
          break;
        
        case 'multiple':
          if (schemas && (schemas as any[]).length > 0) {
            schema = schemas;
          }
          break;
      }

      if (schema) {
        console.log('Injecting schema for type:', type);
        injectSchema(schema);
      }

      // Cleanup function to remove schema when component unmounts
      return () => {
        const existingScript = document.querySelector('script[type="application/ld+json"]');
        if (existingScript) {
          existingScript.remove();
        }
      };
    } catch (error) {
      console.error('Error in useSchema hook:', error);
    }
  }, [type, location.pathname, Array.isArray(schemas) ? schemas.length : 0]);
};

export default useSchema;